'use client'

import { useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { Copy, Loader2, MailCheck, RotateCcw, RotateCw, Save, Send, XCircle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { apiClient } from '@/lib/api/client'
import type { HelmStaffInvitation, HelmUser, HelmUserRole } from '@/lib/api/types'

const roles: Array<{ value: HelmUserRole; label: string }> = [
  { value: 'admin', label: 'Admin' },
  { value: 'forwarder', label: 'Forwarder' },
  { value: 'accounts', label: 'Accounts' },
  { value: 'customer', label: 'Customer' },
  { value: 'viewer', label: 'Viewer' },
]

function formatRole(role: HelmUserRole) {
  return roles.find((item) => item.value === role)?.label ?? role
}

function formatDate(value?: string | null) {
  if (!value) return '-'
  return new Date(value).toLocaleDateString()
}

/** Kampala time, as the team works in it. */
function formatDateTime(value: string) {
  return new Date(value).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Africa/Kampala',
  })
}

function getRoleValues(user: HelmUser) {
  return user.helm_roles.map((assignment) => assignment.role)
}

function RoleBadges({ roles: roleValues }: { roles: HelmUserRole[] }) {
  if (roleValues.length === 0) {
    return <span className="text-muted-foreground">No Helm role</span>
  }

  return (
    <div className="flex flex-wrap gap-1">
      {roleValues.map((role) => (
        <Badge key={role} variant={role === 'admin' ? 'default' : 'secondary'}>
          {formatRole(role)}
        </Badge>
      ))}
    </div>
  )
}

function UserRoleEditor({
  user,
  currentUserId,
  onChanged,
}: {
  user: HelmUser
  currentUserId: string
  onChanged: () => void
}) {
  const initialRoles = useMemo(() => getRoleValues(user), [user])
  const [selectedRoles, setSelectedRoles] = useState<HelmUserRole[]>(initialRoles)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const hasChanged =
    selectedRoles.slice().sort().join(',') !== initialRoles.slice().sort().join(',')
  const removesOwnAdmin = user.id === currentUserId && !selectedRoles.includes('admin')

  function toggleRole(role: HelmUserRole, checked: boolean) {
    setSelectedRoles((current) => {
      if (checked) {
        return current.includes(role) ? current : [...current, role]
      }

      return current.filter((item) => item !== role)
    })
  }

  async function saveRoles() {
    setIsSaving(true)
    setError(null)

    try {
      await apiClient.updateHelmUserRoles(user.id, { roles: selectedRoles })
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update roles')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="space-y-3">
      {/* Plain checkboxes that wrap inside the column; bordered tiles overflowed the card. */}
      <div className="flex max-w-[26rem] flex-wrap gap-x-4 gap-y-2">
        {roles.map((role) => (
          <label key={role.value} className="inline-flex items-center gap-2 text-sm">
            <Checkbox
              checked={selectedRoles.includes(role.value)}
              onCheckedChange={(checked) => toggleRole(role.value, checked === true)}
            />
            <span>{role.label}</span>
          </label>
        ))}
      </div>

      {removesOwnAdmin && (
        <p className="text-sm text-destructive">You cannot remove your own admin role.</p>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex gap-2">
        <Button size="xs" onClick={saveRoles} disabled={!hasChanged || isSaving || removesOwnAdmin}>
          {isSaving ? <Loader2 className="animate-spin" /> : <Save />}
          Save
        </Button>
        <Button
          type="button"
          size="xs"
          variant="outline"
          disabled={!hasChanged || isSaving}
          onClick={() => setSelectedRoles(initialRoles)}
        >
          <RotateCcw />
          Reset
        </Button>
      </div>
    </div>
  )
}

function InviteStaffForm({ onChanged }: { onChanged: () => void }) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<HelmUserRole>('viewer')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [createdInvitation, setCreatedInvitation] = useState<HelmStaffInvitation | null>(null)

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setIsSubmitting(true)
    setError(null)
    setCreatedInvitation(null)

    try {
      const invitation = await apiClient.inviteHelmUser({ email: email.trim(), role })
      setCreatedInvitation(invitation)
      setEmail('')
      setRole('viewer')
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to invite user')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function copyInviteUrl() {
    if (!createdInvitation?.invitation_url) return
    await navigator.clipboard.writeText(createdInvitation.invitation_url)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invite User</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="grid gap-4 md:grid-cols-[1fr_220px_auto]">
            <div className="space-y-2">
              <Label htmlFor="invite_email">Email</Label>
              <Input
                id="invite_email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                placeholder="teammate@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite_role">Role</Label>
              <Select value={role} onValueChange={(value) => setRole(value as HelmUserRole)}>
                <SelectTrigger id="invite_role" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto">
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Invite
              </Button>
            </div>
          </div>

          {createdInvitation && <InviteResult invitation={createdInvitation} />}

          {createdInvitation?.status === 'PENDING' && createdInvitation.invitation_url && (
            <div className="space-y-2">
              <Label htmlFor="invitation_url">Invitation link</Label>
              <div className="flex gap-2">
                <Input id="invitation_url" value={createdInvitation.invitation_url} readOnly />
                <Button type="button" variant="outline" onClick={copyInviteUrl}>
                  <Copy className="h-4 w-4" />
                  Copy
                </Button>
              </div>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  )
}

/** Says plainly what happened after inviting: who was emailed, or why not. */
function InviteResult({ invitation }: { invitation: HelmStaffInvitation }) {
  const email = invitation.invited_email
  if (invitation.email_error) {
    return (
      <Alert variant="destructive" role="status">
        <AlertDescription>
          The invitation for {email} is ready, but the email could not be sent (
          {invitation.email_error}). Copy the link below and send it yourself, or try Resend in the
          list.
        </AlertDescription>
      </Alert>
    )
  }
  return (
    <Alert role="status">
      <MailCheck className="h-4 w-4" />
      <AlertDescription>
        {invitation.status === 'ACCEPTED'
          ? `${email} already had a Helm account and now has ${formatRole(invitation.role)} access. We emailed them.`
          : `Invitation emailed to ${email}. It shows here as pending until they accept.`}
      </AlertDescription>
    </Alert>
  )
}

/** Whether the invitation email went out, and when. */
function EmailStatus({ invitation }: { invitation: HelmStaffInvitation }) {
  if (invitation.email_sent_at) {
    const times = (invitation.email_send_count ?? 1) > 1 ? ` (${invitation.email_send_count}×)` : ''
    return (
      <span className="text-sm">
        Sent {formatDateTime(invitation.email_sent_at)}
        {times}
      </span>
    )
  }
  if (invitation.email_error) {
    return (
      <span className="text-sm text-destructive" title={invitation.email_error}>
        Not sent
      </span>
    )
  }
  return <span className="text-sm text-muted-foreground">Not sent</span>
}

function InvitationStatusBadge({ invitation }: { invitation: HelmStaffInvitation }) {
  if (invitation.status === 'ACCEPTED') {
    return <Badge variant="secondary">Accepted</Badge>
  }

  if (invitation.status === 'REJECTED') {
    return <Badge variant="outline">Revoked</Badge>
  }

  return <Badge>Pending</Badge>
}

function PendingInvitations({
  invitations,
  onChanged,
}: {
  invitations: HelmStaffInvitation[]
  onChanged: () => void
}) {
  const [revokingId, setRevokingId] = useState<string | null>(null)
  const [resendingId, setResendingId] = useState<string | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function resendInvitation(invitation: HelmStaffInvitation) {
    setResendingId(invitation.id)
    setError(null)
    try {
      const sent = await apiClient.resendHelmInvitation(invitation.id)
      if (sent.email_error) {
        setError(`Could not email ${invitation.invited_email}: ${sent.email_error}`)
      }
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to send the invitation')
    } finally {
      setResendingId(null)
    }
  }

  async function copyLink(invitation: HelmStaffInvitation) {
    if (!invitation.invitation_url) return
    await navigator.clipboard.writeText(invitation.invitation_url)
    setCopiedId(invitation.id)
    window.setTimeout(() => setCopiedId(null), 2000)
  }

  async function revokeInvitation(id: string) {
    setRevokingId(id)
    setError(null)

    try {
      await apiClient.revokeHelmInvitation(id)
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to revoke invitation')
    } finally {
      setRevokingId(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invitations</CardTitle>
      </CardHeader>
      {/* No side padding: the table's outer cells line up with the card title. */}
      <CardContent className="space-y-4 px-0">
        {error && (
          <Alert variant="destructive" className="mx-6 w-auto">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {invitations.length === 0 ? (
          <p className="px-6 text-sm text-muted-foreground">No invitations yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invitations.map((invitation) => (
                <TableRow key={invitation.id}>
                  <TableCell className="font-medium">{invitation.invited_email}</TableCell>
                  <TableCell>{formatRole(invitation.role)}</TableCell>
                  <TableCell>
                    <InvitationStatusBadge invitation={invitation} />
                  </TableCell>
                  <TableCell>
                    <EmailStatus invitation={invitation} />
                  </TableCell>
                  <TableCell>{formatDate(invitation.created_at)}</TableCell>
                  <TableCell className="text-right">
                    {invitation.status === 'PENDING' ? (
                      <div className="flex justify-end gap-2">
                        <Button
                          size="xs"
                          variant="outline"
                          onClick={() => void resendInvitation(invitation)}
                          disabled={resendingId === invitation.id}
                          aria-label={`Resend the invitation to ${invitation.invited_email}`}
                        >
                          {resendingId === invitation.id ? (
                            <Loader2 className="animate-spin" />
                          ) : (
                            <RotateCw />
                          )}
                          Resend
                        </Button>
                        {invitation.invitation_url && (
                          <Button
                            size="xs"
                            variant="ghost"
                            onClick={() => void copyLink(invitation)}
                            aria-label={`Copy the invitation link for ${invitation.invited_email}`}
                          >
                            <Copy />
                            {copiedId === invitation.id ? 'Copied' : 'Copy link'}
                          </Button>
                        )}
                        <Button
                          size="xs"
                          variant="outline"
                          onClick={() => revokeInvitation(invitation.id)}
                          disabled={revokingId === invitation.id}
                        >
                          {revokingId === invitation.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <XCircle className="h-4 w-4" />
                          )}
                          Revoke
                        </Button>
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}

export function StaffUserManagement({
  users,
  invitations,
  currentUserId,
  onChanged,
}: {
  users: HelmUser[]
  invitations: HelmStaffInvitation[]
  currentUserId: string
  /** Fetches the users and invitations again, without reloading the page. */
  onChanged?: () => void
}) {
  const router = useRouter()
  const refresh = onChanged ?? (() => router.refresh())
  return (
    <div className="space-y-6">
      <InviteStaffForm onChanged={refresh} />

      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {users.length === 0 ? (
            <p className="px-6 text-sm text-muted-foreground">No users yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Current Roles</TableHead>
                  <TableHead>Last Seen</TableHead>
                  <TableHead className="w-[420px]">Manage Roles</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="font-medium">
                        {user.display_name || user.name || user.email}
                      </div>
                      <div className="text-sm text-muted-foreground">{user.email}</div>
                    </TableCell>
                    <TableCell>
                      <RoleBadges roles={getRoleValues(user)} />
                    </TableCell>
                    <TableCell>{formatDate(user.last_seen_at)}</TableCell>
                    <TableCell>
                      <UserRoleEditor
                        user={user}
                        currentUserId={currentUserId}
                        onChanged={refresh}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PendingInvitations invitations={invitations} onChanged={refresh} />
    </div>
  )
}
